import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Unified button design system (ATÔ):
 * - ONE shape for every variant: rounded-lg (~9px, via --radius CSS var).
 * - primary   (default) — solid brand green, main action of a page.
 * - secondary (alias: outline) — transparent + green border, secondary action.
 * - danger    (alias: destructive) — solid red, irreversible actions.
 * - accent    — solid orange, rare/strategic conversion CTA (e.g. header Register).
 * All colors resolve through CSS variables set by the Admin > Appearance panel.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-white shadow-sm hover:bg-primary-hover",
        primary: "bg-primary text-white shadow-sm hover:bg-primary-hover",
        secondary: "bg-transparent border border-primary text-primary hover:bg-primary/5",
        outline: "bg-transparent border border-border text-text_main hover:border-primary hover:text-primary hover:bg-primary/5",
        danger: "bg-danger text-white shadow-sm hover:bg-danger-hover",
        destructive: "bg-danger text-white shadow-sm hover:bg-danger-hover",
        accent: "bg-accent text-white shadow-sm hover:bg-accent-hover",
        success: "bg-success text-white shadow-sm hover:bg-success/90",
        ghost: "hover:bg-surface_muted hover:text-text_main",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        md: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
